const positiveInteger = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const nonNegativeMinor = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const nonNegativeQuantity = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const majorToMinor = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round((parsed + Number.EPSILON) * 100) : null;
};

const firstMinor = (...values) => values.find((value) => value !== null && value !== undefined) ?? 0;

const normalizedCurrency = (...values) => {
  const currency = values.find((value) => typeof value === "string" && /^[A-Za-z]{3}$/.test(value.trim()));
  return (currency || "AUD").trim().toUpperCase();
};

const explicitAmountMinor = (value) => nonNegativeMinor(value?.amount_minor ?? value?.amountMinor);

const normalizeService = (service) => {
  if (!service || typeof service !== "object") return null;
  const id = positiveInteger(service.service_id ?? service.id);
  if (!id) return null;
  const includedQuantity = positiveInteger(service.included_quantity ?? service.includedQuantity ?? service.qty);
  const used = service.is_used === true || service.is_used === 1 || service.isUsed === true || service.isUsed === 1;
  return {
    id,
    name: String(service.name_snapshot || service.service_name_snapshot || service.name || `Historical service #${id}`),
    includedQuantity,
    used,
    snapshotQuality: String(service.snapshot_quality || service.snapshotQuality || (service.name_snapshot || service.service_name_snapshot ? "snapshot" : "legacy_current_catalog")),
  };
};

const normalizeProduct = (product) => {
  if (!product || typeof product !== "object") return null;
  const nested = product.product && typeof product.product === "object" ? product.product : {};
  const productId = positiveInteger(product.product_id ?? nested.id);
  const entitlementId = positiveInteger(product.entitlement_id ?? product.id);
  if (!productId || !entitlementId) return null;
  const originalQuantity = nonNegativeQuantity(
    product.original_quantity ?? product.original_qty ?? product.quantity ?? product.qty,
  );
  const remainingQuantity = nonNegativeQuantity(
    product.remaining_quantity ?? product.remaining_qty ?? product.pending_quantity ?? product.pending_qty,
  );
  const usedQuantity = nonNegativeQuantity(product.used_quantity ?? product.used_qty) ?? (
    originalQuantity !== null && remainingQuantity !== null
      ? Math.max(0, originalQuantity - remainingQuantity)
      : 0
  );
  const safeOriginal = originalQuantity ?? usedQuantity + (remainingQuantity ?? 0);
  const safeRemaining = remainingQuantity ?? Math.max(0, safeOriginal - usedQuantity);
  const snapshotName = product.product_name_snapshot || product.name_snapshot;
  return {
    entitlementId,
    productId,
    name: String(snapshotName || product.product_name || product.name || nested.name || `Historical product #${productId}`),
    originalQuantity: safeOriginal,
    usedQuantity,
    remainingQuantity: safeRemaining,
    status: String(product.status || (safeRemaining === 0 ? "exhausted" : "active")).toLowerCase(),
    snapshotQuality: String(product.snapshot_quality || (snapshotName ? "snapshot" : "legacy_current_catalog")),
  };
};

const inferredStatus = (source, remainingMinor, products) => {
  const explicit = source.status || source.lifecycle_status;
  if (explicit) return String(explicit).trim().toLowerCase();
  const saleStatus = String(source.sale?.status || "").trim().toLowerCase();
  if (["pending", "unpaid", "processing"].includes(saleStatus)) return "pending_payment";
  if (["cancelled", "canceled", "void", "voided", "refunded"].includes(saleStatus)) return saleStatus === "canceled" ? "cancelled" : saleStatus;
  if (products.some((product) => product.remainingQuantity > 0 && product.status === "active")) return "active";
  if (remainingMinor === 0) return "exhausted";
  if (["paid", "completed"].includes(saleStatus) || source.membershipcreditmanage?.is_active === true || source.membershipcreditmanage?.is_active === 1) return "active";
  return "unknown";
};

export const normalizePackageBalance = (sourceValue = {}) => {
  const source = sourceValue?.data && !Array.isArray(sourceValue.data) ? sourceValue.data : sourceValue;
  const legacyCredit = source.membershipcreditmanage || {};
  const membership = source.membership || {};
  const modernBalance = source.balance || {};
  const legacyDtoCredits = source.credits || {};
  const currency = normalizedCurrency(
    modernBalance.currency,
    modernBalance.original?.currency,
    source.cost?.currency,
    source.currency,
  );
  const originalMinor = firstMinor(
    nonNegativeMinor(modernBalance.original_minor ?? modernBalance.originalMinor),
    explicitAmountMinor(modernBalance.original),
    majorToMinor(legacyDtoCredits.total),
    majorToMinor(legacyCredit.credit),
    majorToMinor(membership.credit),
  );
  const remainingMinor = firstMinor(
    nonNegativeMinor(modernBalance.remaining_minor ?? modernBalance.remainingMinor),
    explicitAmountMinor(modernBalance.remaining),
    majorToMinor(legacyDtoCredits.pending),
    majorToMinor(legacyCredit.pending_credit),
    originalMinor,
  );
  const netUsedMinor = firstMinor(
    nonNegativeMinor(modernBalance.net_used_minor ?? modernBalance.netUsedMinor),
    explicitAmountMinor(modernBalance.net_used || modernBalance.used),
    majorToMinor(legacyDtoCredits.used),
    majorToMinor(legacyCredit.used_credit),
    Math.max(0, originalMinor - remainingMinor),
  );
  const rawServices = Array.isArray(source.included_services)
    ? source.included_services
    : Array.isArray(source.services)
      ? source.services
      : [];
  const rawProducts = Array.isArray(source.included_products)
    ? source.included_products
    : Array.isArray(source.products)
      ? source.products
      : [];
  const products = rawProducts.map(normalizeProduct).filter(Boolean);
  const packageInstanceId = positiveInteger(source.package_instance_id ?? source.packageInstanceId ?? legacyCredit.id);
  const expirySource = source.expiry || {};
  const unattributedMinor = firstMinor(
    nonNegativeMinor(source.usage_summary?.legacy_unattributed_minor),
    nonNegativeMinor(source.unattributed_minor ?? source.unattributedMinor),
  );
  const hasExplicitCost = source.cost && (
    Object.prototype.hasOwnProperty.call(source.cost, "amount_minor") ||
    Object.prototype.hasOwnProperty.call(source.cost, "amountMinor")
  );
  // A saved unknown purchase amount must not become the current catalogue price.
  const purchaseAmountMinor = hasExplicitCost
    ? explicitAmountMinor(source.cost)
    : [majorToMinor(legacyCredit.cost), majorToMinor(membership.cost)]
      .find((value) => value !== null) ?? null;

  return {
    id: positiveInteger(source.id),
    packageInstanceId,
    membershipId: positiveInteger(membership.id ?? source.membership_id),
    name: String(source.membership_name_snapshot || membership.name_snapshot || membership.name || "Historical package"),
    nameSnapshotQuality: String(source.snapshot_quality || source.name_snapshot_quality || (source.membership_name_snapshot || membership.name_snapshot ? "snapshot" : "legacy_current_catalog")),
    purchaseAmount: {
      amountMinor: purchaseAmountMinor,
      currency,
    },
    balance: { currency, originalMinor, netUsedMinor, remainingMinor },
    status: inferredStatus(source, remainingMinor, products),
    expiry: {
      status: String(expirySource.status || source.expiry_status || "not_configured"),
      expiresAt: expirySource.expires_at ?? expirySource.expiresAt ?? source.expires_at ?? null,
    },
    purchasedAt: source.purchase?.purchased_at || source.purchased_at || source.created_at || source.sale?.invoicedate || source.sale?.invoice_date || source.sale?.created_at || null,
    purchaseSale: source.purchase?.sale || source.sale || null,
    services: rawServices.map(normalizeService).filter(Boolean),
    products,
    attributionAvailable: packageInstanceId !== null,
    unattributedMinor,
  };
};

const normalizeUsageEvent = (event) => {
  const source = event && typeof event === "object" ? event : {};
  const type = String(source.event_type || source.type || "unknown").toLowerCase();
  const service = source.service && typeof source.service === "object" ? {
    id: positiveInteger(source.service.id ?? source.service.service_id),
    name: String(source.service.name_snapshot || source.service.name || "Historical service"),
  } : null;
  const sale = source.sale && typeof source.sale === "object" ? {
    id: positiveInteger(source.sale.id),
    invoiceNumber: source.sale.invoice_number || source.sale.invoiceNumber || null,
    status: source.sale.status ? String(source.sale.status) : null,
  } : null;
  const appointment = source.appointment && typeof source.appointment === "object" ? {
    id: positiveInteger(source.appointment.id),
    startsAt: source.appointment.starts_at || source.appointment.startsAt || source.appointment.start_datetime || null,
    status: source.appointment.status ? String(source.appointment.status) : null,
  } : null;
  const currency = normalizedCurrency(source.amount?.currency, source.currency);
  return {
    id: positiveInteger(source.id) || `${type}-${source.occurred_at || "legacy"}`,
    type,
    amount: {
      amountMinor: firstMinor(explicitAmountMinor(source.amount), nonNegativeMinor(source.amount_minor ?? source.amountMinor)),
      currency,
    },
    service,
    sale,
    appointment,
    occurredAt: source.occurred_at || source.occurredAt || null,
    reversesUsageId: positiveInteger(source.reverses_usage_id ?? source.reversesUsageId),
  };
};

export const normalizePackageUsagePage = (value = {}) => {
  const source = value?.data && !Array.isArray(value.data) ? value.data : value;
  const rows = Array.isArray(source.data) ? source.data : Array.isArray(source.events) ? source.events : [];
  return {
    currentPage: positiveInteger(source.current_page ?? source.currentPage) || 1,
    data: rows.map(normalizeUsageEvent),
    nextPageUrl: source.next_page_url ?? source.nextPageUrl ?? null,
    total: nonNegativeMinor(source.total) || rows.length,
  };
};

export const formatPackageMoney = ({ amountMinor = 0, currency = "AUD" } = {}) => {
  if (amountMinor === null) return "—";
  const safeMinor = nonNegativeMinor(amountMinor) ?? 0;
  const safeCurrency = normalizedCurrency(currency);
  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency: safeCurrency }).format(safeMinor / 100);
  } catch (_error) {
    return `${safeCurrency} ${(safeMinor / 100).toFixed(2)}`;
  }
};

export const packageStatusLabel = (status) => ({
  active: "Active",
  inactive: "Inactive",
  exhausted: "Exhausted",
  pending_payment: "Pending payment",
  cancelled: "Cancelled",
  void: "Void",
  voided: "Void",
  refunded: "Refunded",
  unknown: "Status unavailable",
}[status] || "Status unavailable");
